import LaunchIcon from '@mui/icons-material/Launch';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as Sentence } from "../../../../../assets/icons/sentence.svg";
import { DashboardsContext } from '../../../../../contexts/DashboardsContext';
import { getDashboardQueriesFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { Dashboard, DashboardQuery } from '../../../../../types/files';
import DescriptionModal from '../../../../modals/tables/dashboards/rows/DescriptionModal';
import useStyles from "../styles";

export const useLocalStyles = makeStyles((theme) => ({
    text: {
        display: '-webkit-box',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['600'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
}));

const DescriptionCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const localClasses = useLocalStyles();
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);

    const [descriptionLoaded, setDescriptionLoaded] = useState<boolean>(false);
    const [description, setDescription] = useState<string>('');
    const [descModalOpen, setDescModalOpen] = useState<boolean>(false);
    const [fieldHover, setFieldHover] = useState<boolean>(false);
    const descriptionRef = useRef<HTMLElement>(null);

    const queryKey = useMemo(() => `${dashboard.id}:Description`, [dashboard.id]);

    useQuery({
        queryKey: ['dashboardQuery', `${dashboard.id}:Description`],
        queryFn: async () => !mappedOverviewQueries.has(queryKey)
            ? await getDashboardQueriesFunc(dashboard.id, 'Description') : [],
        staleTime: 5 * 60 * 1000,
        retry: 2,
        onSuccess: (bulkQueryData: DashboardQuery[]) => {
            setDashboardsQueries(prev => produce(prev, draft => {
                bulkQueryData.forEach(newQuery => {
                    if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
                        draft.push(newQuery);
                });
            }));
            setDescriptionLoaded(true);
        },
    });

    useEffect(() => {
        if (mappedOverviewQueries.has(queryKey)) {
            setDescription(mappedOverviewQueries.get(queryKey) || '');
            setDescriptionLoaded(true);
        }
    // eslint-disable-next-line
    }, [mappedOverviewQueries]);

    return (<>
        <Stack width="100%" ref={descriptionRef} onMouseLeave={() => setFieldHover(false)}>
            {descriptionLoaded ? (<>
                {(!description || description === 'N/A') ? (
                    <Typography className={classes.plainText} fontWeight={500} color={`${theme.colors.neutral['500']} !important` }>
                        {'No description'}
                    </Typography>
                ) : (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                        onMouseEnter={() => setFieldHover(true)}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                            <Sentence style={{ flexShrink: 0, stroke: 'rgb(163, 167, 172)' }} />
                            <Typography className={localClasses.text}>
                                {description}
                            </Typography>
                        </Stack>
                        {fieldHover && (
                            <Stack direction="row" alignItems="stretch" justifyContent="flex-end">
                                <IconButton disableRipple
                                    onClick={() => setDescModalOpen(true)}
                                    sx={{ padding: 'unset !important'}}>
                                    <LaunchIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                                </IconButton>
                            </Stack>
                        )}
                    </Stack>
                )}
            </>) : (
                <Box sx={{ width: '100%' }}>
                    <Skeleton animation="wave" className={classes.wave} />
                </Box>
            )}
        </Stack>
        {descModalOpen && (
            <DescriptionModal open={descModalOpen}
                dashboard={dashboard}
                anchorElement={descriptionRef.current}
                onClose={() => setDescModalOpen(false)} />
        )}
    </>);
}

export default memo(DescriptionCell);
